import { type FC, useEffect, memo, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly.ts';
import { getBuyerId, getEverLoggedIn } from 'dibs-cookie-jar';
import { useWalletConnectionEnsured } from 'dibs-crypto-utils/exports/useWalletConnectionEnsured.ts';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';

import { UnsupportedChainContent } from './UnsupportedChainContent.tsx';
import { UnsupportedBrowserContent } from './UnsupportedBrowserContent.tsx';
import { MismatchedAddressContent } from './MismatchedAddressContent.tsx';
import { ConnectWalletWithSigningContent } from './ConnectWalletWithSigningContent.tsx';
import { ConnectWalletWithSigningNoZipContent } from './ConnectWalletWithSigningNoZipContent.tsx';
import { ConnectWalletContent } from './ConnectWalletContent.tsx';
import * as contentTypes from './contentTypes.ts';
import { type ContentType } from './contentTypes.ts';

import { type WithWalletConnectionEnsured_viewer$key } from './__generated__/WithWalletConnectionEnsured_viewer.graphql.ts';

// TODO: move to auth modal in DBL
type AuthResponse = {
    user: {
        serviceId: string;
        ethereumWalletAddress: {
            address: string;
        };
    };
};

type Props = {
    viewer: WithWalletConnectionEnsured_viewer$key;
    isMobile: boolean;
    persistWalletWithoutBidderRole?: boolean;
    onAuthComplete?: (authResponse: AuthResponse) => void | Promise<void>;
    onWalletPersisted?: () => void;
    onWalletConnected?: () => void;
    setContentTypeLabel?: (contentType: string) => void;
    trackingLabel?: string;
    metamaskConnectTrackingLabel?: string;
    authFlow?: string;
    authAction?: string | null;
    children: ReactNode;
};

export const WithWalletConnectionEnsured: FC<Props> = memo(
    ({
        viewer: viewerRef,
        isMobile,
        persistWalletWithoutBidderRole = false,
        onAuthComplete = () => {},
        onWalletPersisted = () => {},
        onWalletConnected = () => {},
        setContentTypeLabel = () => {},
        trackingLabel = '',
        metamaskConnectTrackingLabel = '',
        authFlow,
        authAction,
        children,
    }) => {
        const viewer = useFragment(
            graphql`
                fragment WithWalletConnectionEnsured_viewer on Viewer {
                    walletConnectEnabled: featureFlag(feature: "allowWalletConnect")
                    ...useWalletConnectionEnsured_viewer
                    ...UnsupportedBrowserContent_viewer
                    ...UnsupportedChainContent_viewer
                    ...MismatchedAddressContent_viewer
                    ...ConnectWalletWithSigningContent_viewer
                    ...ConnectWalletWithSigningNoZipContent_viewer
                    ...ConnectWalletContent_viewer
                }
            `,
            viewerRef
        );
        const { awaitingAddressAndBidderRole, isMetaMaskInstalled, switchMetaMaskEthereumChain } =
            useWeb3Connection();
        const {
            isMetamaskUnsupported,
            isUnsupportedChain,
            hasAddressMismatch,
            persistedAddress,
            isAddressPersisted,
            userId,
            isWalletConnected,
        } = useWalletConnectionEnsured({ viewer, isMobile });

        const isLoggedIn = !!getBuyerId(document.cookie);
        const everLoggedIn = !!getEverLoggedIn(document.cookie);
        const emailOnly = isEmailOnly(document.cookie);

        let contentType: ContentType = '';
        let component = null;
        let isConnected = false;

        if (!isLoggedIn || (isLoggedIn && emailOnly)) {
            let action = authAction;

            if (emailOnly) {
                action = authModalLoader.constants.REQUEST_PASSWORD_EMAIL;
            } else if (!action) {
                action = everLoggedIn
                    ? authModalLoader.constants.LOGIN
                    : authModalLoader.constants.REGISTER;
            }
            contentType = action;
            component = authModalLoader.loadAuthContent({
                action,
                flow: authFlow,
                onAuthComplete,
                ga: {
                    label: trackingLabel,
                },
            });
        } else if (isMetamaskUnsupported && !viewer.walletConnectEnabled) {
            contentType = contentTypes.UNSUPPORTED_BROWSER;
            component = <UnsupportedBrowserContent isMobile={isMobile} viewer={viewer} />;
        } else if (isUnsupportedChain) {
            contentType = contentTypes.UNSUPPORTED_CHAIN;
            component = (
                <UnsupportedChainContent
                    viewer={viewer}
                    isMetaMaskInstalled={isMetaMaskInstalled}
                    switchMetaMaskEthereumChain={switchMetaMaskEthereumChain}
                />
            );
        } else if (hasAddressMismatch) {
            contentType = contentTypes.MISMATCHED_ADDRESS;
            component = (
                <MismatchedAddressContent
                    persistedAddress={persistedAddress ?? null}
                    viewer={viewer}
                />
            );
        } else if (!isAddressPersisted && persistWalletWithoutBidderRole) {
            contentType = contentTypes.CONNECT_METAMASK_WITH_SIGNING;
            component = (
                <ConnectWalletWithSigningNoZipContent
                    userId={userId}
                    trackingLabel={metamaskConnectTrackingLabel}
                    viewer={viewer}
                    onWalletPersisted={onWalletPersisted}
                />
            );
        } else if (!isAddressPersisted || awaitingAddressAndBidderRole) {
            contentType = contentTypes.CONNECT_METAMASK_WITH_SIGNING;
            component = (
                <ConnectWalletWithSigningContent
                    userId={userId}
                    trackingLabel={metamaskConnectTrackingLabel}
                    viewer={viewer}
                    onWalletPersisted={onWalletPersisted}
                />
            );
        } else if (!isWalletConnected) {
            contentType = contentTypes.CONNECT_METAMASK;
            component = (
                <ConnectWalletContent
                    trackingLabel={metamaskConnectTrackingLabel}
                    viewer={viewer}
                />
            );
        } else {
            isConnected = true;
            component = <>{children}</>;
        }

        useEffect(() => {
            if (isConnected) {
                onWalletConnected();
            }
        }, [isConnected, onWalletConnected]);

        // setContentTypeLabel changes parent state, so it's a side effect
        useEffect(() => {
            setContentTypeLabel(contentType);
        });

        return component;
    }
);
